import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import uniqueId from "lodash/uniqueId";

import { useGetCompetitors } from "app/pages/sales-intelligence/modules/ai-agent/queries/useGetCompetitors";
import { useGetMultipleContacts } from "app/pages/sales-intelligence/modules/ai-agent/queries/useGetMultipleContacts";
import type {
    AiAgentMeetingPayload,
    Competitor,
    Contact,
    FullThreadDto,
} from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import {
    createNewContact,
    MAX_DISPLAYED_CONTACTS,
    parseContactString,
} from "app/pages/sales-intelligence/pages/ai-agent/containers/Attendees/utils";
import {
    getContactsKey,
    hasCompetitorsChanged,
    isContactMatchedByAttendee,
    mapFormValueToSelectedContacts,
    mapMeetingGoalToFormValue,
    mapSelectedContactsToFormValue,
    parseAttendeeNames,
    processContactsWithSelection,
} from "app/pages/sales-intelligence/pages/ai-agent/utils/firstRespondFormUtils";

const NUM_DISPLAYED_COMPETITORS = 4;

export type FirstRespondFormValues = Partial<{
    meetingGoal: string | undefined;
    meetingGoalOption: string | undefined;
    attendees: string | undefined;
    competitors: Competitor[] | undefined;
    otherNotes: string | undefined;
    attendeesContacts: Contact[] | undefined;
}>;

export type FirstRespondFormType = {
    values: FirstRespondFormValues;
    onUpdate: <K extends keyof FirstRespondFormValues>(key: K, value: FirstRespondFormValues[K]) => void;
};

export const useFirstRespondForm = (
    companyName: string,
    threadData?: FullThreadDto,
): {
    form: FirstRespondFormType;
    defaultCompetitors: Competitor[];
    createFirstFormPayload: () => AiAgentMeetingPayload;
    createAutogeneratePayload: () => AiAgentMeetingPayload;
    isLoading: boolean;
} => {
    const {
        params: { domain, isCompanyLevel, id, attendee },
    } = useCurrentStateAndParams<{
        domain: string;
        isCompanyLevel?: boolean;
        id?: string;
        attendee?: string;
    }>();

    const [values, setValues] = useState<FirstRespondFormValues>({
        attendeesContacts: [],
    });

    const newFormContactsRef = useRef<Contact[]>([]);
    const isExistingThreadEnrichedRef = useRef(false);

    const enableFetch = !id && Boolean(domain);

    const attendeeNames = useMemo(() => parseAttendeeNames(attendee), [attendee]);

    const contactNamesToFetch = useMemo(() => {
        if (threadData?.meetingContext?.attendees) {
            return threadData.meetingContext.attendees
                .split(";")
                .map((entry) => parseContactString(entry).name)
                .filter(Boolean);
        }
        return attendeeNames;
    }, [attendeeNames, threadData?.meetingContext?.attendees]);

    const { data: competitorsData, isLoading: isCompetitorsLoading } = useGetCompetitors(domain, enableFetch);

    const { data: attendeesContacts, isLoading: isAttendeesLoading } = useGetMultipleContacts({
        domain,
        attendeeNames: contactNamesToFetch,
        enabled: id ? contactNamesToFetch.length > 0 : enableFetch,
    });

    useEffect(() => {
        if (threadData) {
            return;
        }

        if (Array.isArray(competitorsData)) {
            const newCompetitors = competitorsData.slice(0, NUM_DISPLAYED_COMPETITORS);
            setValues((prevValues) => {
                const prevCompetitors = prevValues.competitors || [];

                if (!hasCompetitorsChanged(prevCompetitors, newCompetitors)) {
                    return prevValues;
                }

                return {
                    ...prevValues,
                    competitors: newCompetitors,
                };
            });
        }
    }, [competitorsData, threadData]);

    useEffect(() => {
        if (threadData) {
            newFormContactsRef.current = [];
            return;
        }

        if (!Array.isArray(attendeesContacts) || attendeesContacts.length === 0) {
            return;
        }

        const contactsKey = getContactsKey(attendeesContacts);
        const processedKey = getContactsKey(newFormContactsRef.current);

        if (contactsKey === processedKey) {
            return;
        }

        setValues((prevValues) => {
            const prevContacts = prevValues.attendeesContacts || [];
            const newContacts = processContactsWithSelection(attendeesContacts, attendeeNames, prevContacts);

            newFormContactsRef.current = attendeesContacts;

            return {
                ...prevValues,
                attendeesContacts: newContacts,
            };
        });
    }, [attendeeNames, attendeesContacts, threadData]);

    useEffect(() => {
        if (threadData) {
            setValues((prevValues) => ({
                ...prevValues,
                meetingGoal: threadData.meetingContext.goal,
                meetingGoalOption: mapMeetingGoalToFormValue(threadData.meetingContext.goal),
                attendees: threadData.meetingContext.attendees,
                competitors: threadData.meetingContext.competitors,
                otherNotes: threadData.meetingContext.otherNotes,
                // Only set attendeesContacts if enrichment hasn't happened yet
                ...(isExistingThreadEnrichedRef.current
                    ? {}
                    : { attendeesContacts: mapFormValueToSelectedContacts(threadData.meetingContext.attendees) }),
            }));
        }
    }, [threadData]);

    // Enrich threadData contacts with source information from API
    useEffect(() => {
        if (
            !id ||
            !threadData?.meetingContext?.attendees ||
            !Array.isArray(attendeesContacts) ||
            attendeesContacts.length === 0 ||
            isAttendeesLoading
        ) {
            return;
        }

        if (isExistingThreadEnrichedRef.current) {
            return;
        }

        isExistingThreadEnrichedRef.current = true;

        const parsedContacts = threadData.meetingContext.attendees
            .split(";")
            .filter(Boolean)
            .map((contactString) => {
                const { name, title } = parseContactString(contactString);
                const matchedContact = attendeesContacts.find(
                    (fetched) => fetched.name?.toLowerCase() === name?.toLowerCase(),
                );
                return createNewContact({
                    name,
                    title,
                    source: matchedContact?.source,
                });
            });

        setValues((prevValues) => ({
            ...prevValues,
            attendeesContacts: parsedContacts,
        }));
    }, [id, threadData?.meetingContext?.attendees, attendeesContacts, isAttendeesLoading]);

    const createFirstFormPayload = useCallback((): AiAgentMeetingPayload => {
        const selectedContacts =
            values.attendeesContacts?.filter((contact) => contact.isSelected).slice(0, MAX_DISPLAYED_CONTACTS) || [];

        return {
            domain,
            companyName,
            meetingContext: {
                attendees:
                    (selectedContacts.length > 0
                        ? mapSelectedContactsToFormValue(selectedContacts)
                        : values.attendees) || "",
                goal: values.meetingGoal || "",
                competitors: values.competitors || [],
                isCompanyLevel: Boolean(isCompanyLevel),
                otherNotes: values.otherNotes || "",
                attendeesCount: selectedContacts.length,
            },
        };
    }, [companyName, domain, values, isCompanyLevel]);

    const createAutogeneratePayload = useCallback((): AiAgentMeetingPayload => {
        // Use raw data for autogenerate since form state might not be ready yet
        const enrichedContacts = Array.isArray(attendeesContacts)
            ? attendeesContacts
                  .filter((contact) => attendeeNames.some((name) => isContactMatchedByAttendee(contact, name)))
                  .map((contact) => ({
                      ...contact,
                      isSelected: true,
                      id: uniqueId(),
                      isSuggested: true,
                  }))
            : [];

        const missingNames = attendeeNames.filter(
            (attendee) => !enrichedContacts.some((contact) => isContactMatchedByAttendee(contact, attendee)),
        );

        const manuallyCreatedContacts = missingNames.map((name) => ({
            ...createNewContact({ name: name.trim(), title: "" }),
            isSelected: true,
            isSuggested: false,
        }));

        const attendeesContactsValue = [...enrichedContacts, ...manuallyCreatedContacts].slice(
            0,
            MAX_DISPLAYED_CONTACTS,
        );

        const defaultCompetitorsList = Array.isArray(competitorsData)
            ? competitorsData.slice(0, NUM_DISPLAYED_COMPETITORS)
            : [];

        const attendeesValue =
            attendeesContactsValue.length > 0 ? mapSelectedContactsToFormValue(attendeesContactsValue) : "";
        const attendeesCount = attendeesContactsValue.length;

        setValues((prevValues) => ({
            ...prevValues,
            attendees: attendeesValue,
            competitors: defaultCompetitorsList,
            attendeesContacts: attendeesContactsValue,
        }));

        return {
            domain,
            companyName,
            calendarAttendees: attendee,
            meetingContext: {
                attendees: attendeesValue,
                goal: "",
                competitors: defaultCompetitorsList,
                isCompanyLevel: Boolean(isCompanyLevel),
                otherNotes: "",
                attendeesCount,
            },
        };
    }, [attendeesContacts, attendeeNames, competitorsData, companyName, domain, isCompanyLevel, attendee]);

    return {
        form: {
            values,
            onUpdate: (key, value) => setValues((prevState) => ({ ...prevState, [key]: value })),
        },
        createFirstFormPayload,
        createAutogeneratePayload,
        defaultCompetitors: Array.isArray(competitorsData) ? competitorsData : [],
        isLoading: isCompetitorsLoading || isAttendeesLoading,
    };
};
